import { createContext, useContext, useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'atoms/Toast'
import {
  TranslationMap,
  TranslationMapSpecificationSchema,
} from 'common/types/kraken-core/TranslationMap'
import { DataModelSchemaDetail } from 'common/types/kraken-core/DataModelSchema'
import {
  useGetTranslationMap,
  onExport,
  useUpdateTranslationMapSetting,
  usePush,
  useCreateTranslationMap,
} from '../api'
import { useGetDataModelSchema } from 'pages/DataModelSchema/api'
import { validateFields } from 'common/helpers/kraken-core/translationMaps.helper'
import { useImplementation } from '../hooks/useImplementation'

export type ViewMode = 'simple' | 'advanced'

export interface TranslationMapDetailsContextProps {
  // Core state
  translationMap?: TranslationMap
  isDirty: boolean
  validations?: string
  viewMode: ViewMode
  isLoading: boolean
  error: any
  isUpdating: boolean
  isPushing: boolean
  isCreating: boolean

  // Grouped state
  dataModels: {
    input?: {
      dataModel: DataModelSchemaDetail
      contentType: string
    }
    output?: {
      dataModel: DataModelSchemaDetail
      contentType: string
    }
    loading: { input: boolean; output: boolean }
  }

  dialogs: {
    input: boolean
    output: boolean
    inputStructure: boolean
    outputStructure: boolean
  }

  mappingEditor: {
    activeTab: number
    setActiveTab: (tab: number) => void
  }

  mappingDetails: {
    currentView: 'mapping' | 'testing'
    setCurrentView: (view: 'mapping' | 'testing') => void
  }

  // Core actions
  updateTranslationMap: (updates: Partial<TranslationMap>) => void
  save: () => void
  create: () => void
  export: () => void
  push: () => void
  refetch: () => void
  setViewMode: (mode: ViewMode) => void

  // Dialog actions
  openInputDialog: () => void
  closeInputDialog: () => void
  openOutputDialog: () => void
  closeOutputDialog: () => void
  openInputDataModelStructureDialog: () => void
  closeInputDataModelStructureDialog: () => void
  openOutputDataModelStructureDialog: () => void
  closeOutputDataModelStructureDialog: () => void

  // Data model actions
  updateInputDataModel: (dataModel: any) => void
  updateOutputDataModel: (dataModel: any) => void

  // Artifact actions
  updateArtifactValue: (value: string) => void
  updateArtifactValueFromServer: (value: string) => void
  currentArtifactValue: string
  clearPendingChanges: () => void

  // Specification actions
  updateSpecification: (specification: TranslationMapSpecificationSchema) => void
  updateSpecificationField: (field: string, value: string) => void
  currentSpecification: TranslationMapSpecificationSchema
  isSpecificationDirty: boolean
  clearSpecificationPendingChanges: () => void
}

export const TranslationMapDetailsContext = createContext<
  TranslationMapDetailsContextProps | undefined
>(undefined)

export interface TranslationMapDetailsProviderProps {
  children: React.ReactNode
}

export function TranslationMapDetailsProvider({ children }: TranslationMapDetailsProviderProps) {
  const { id } = useParams<{ id: string }>()

  // Core state
  const [isDirty, setIsDirty] = useState<boolean>(false)
  const [translationMapData, setTranslationMapData] = useState<TranslationMap>()
  const [validations, setValidations] = useState<string>()
  const [viewMode, setViewMode] = useState<ViewMode>('simple')
  const [isCreationMode, setIsCreationMode] = useState<boolean>(!id)

  // Specification state - initialize with empty values to prevent undefined issues
  const [specification, setSpecification] = useState<TranslationMapSpecificationSchema>({
    summary: '',
    data_mapping: '',
  })
  const [serverSpecification, setServerSpecification] = useState<TranslationMapSpecificationSchema>(
    {
      summary: '',
      data_mapping: '',
    }
  )
  const specificationRef = useRef<TranslationMapSpecificationSchema>({})

  // Use the implementation hook
  const implementation = useImplementation(id || '')
  const isUserEditingRef = useRef(false)
  const isSavingRef = useRef(false)

  // Store the functions in refs to avoid dependency issues
  const implementationLoadFromServerRef = useRef(implementation.loadFromServer)

  // Update refs when functions change
  implementationLoadFromServerRef.current = implementation.loadFromServer

  // Data Model Schema States
  const [inputDataModel, setInputDataModel] = useState<DataModelSchemaDetail>()
  const [outputDataModel, setOutputDataModel] = useState<DataModelSchemaDetail>()
  const [isLoadingInputDataModel, setIsLoadingInputDataModel] = useState<boolean>(false)
  const [isLoadingOutputDataModel, setIsLoadingOutputDataModel] = useState<boolean>(false)

  // Dialog State
  const [inputDialogOpen, setInputDialogOpen] = useState<boolean>(false)
  const [outputDialogOpen, setOutputDialogOpen] = useState<boolean>(false)
  const [inputDataModelStructureDialogOpen, setInputDataModelStructureDialogOpen] = useState<
    boolean
  >(false)
  const [outputDataModelStructureDialogOpen, setOutputDataModelStructureDialogOpen] = useState<
    boolean
  >(false)

  // Mapping editor state
  const [activeTab, setActiveTab] = useState<number>(0)

  // Mapping details state
  const [currentView, setCurrentView] = useState<'mapping' | 'testing'>('mapping')

  // API hooks
  const { isLoading, error, data: rawData, refetch } = useGetTranslationMap(id, {
    enabled: !isCreationMode,
  })

  const {
    mutate: updateTranslationMapSetting,
    isLoading: isUpdating,
    isSuccess: isUpdated,
    error: updatedError,
  } = useUpdateTranslationMapSetting()

  const {
    mutate: createTranslationMap,
    isLoading: isCreating,
    isSuccess: isCreated,
    error: createError,
  } = useCreateTranslationMap()

  const {
    mutate: push,
    isLoading: isPushing,
    isSuccess: isPushSuccess,
    isError: isPushError,
    error: pushError,
  } = usePush()

  // Data Model Schema API hooks
  const {
    data: inputDataModelData,
    isLoading: isLoadingInputDataModelData,
  } = useGetDataModelSchema(translationMapData?.input_data_model || '', {
    enabled: Boolean(translationMapData?.input_data_model),
  })

  const {
    data: outputDataModelData,
    isLoading: isLoadingOutputDataModelData,
  } = useGetDataModelSchema(translationMapData?.output_data_model || '', {
    enabled: Boolean(translationMapData?.output_data_model),
  })

  // Refs for stable function references
  const handleExport = useRef<Function>(() => {})
  const handleSave = useRef<Function>(() => {})
  const handleCreate = useRef<Function>(() => {})
  const handlePush = useRef<Function>(() => {})

  // Effects
  useEffect(() => {
    if (rawData) {
      // Don't update during save operations to prevent glitches
      if (isSavingRef.current) {
        console.log('Skipping translationMapData update during save operation')
        return
      }

      // Only update translationMapData if we don't have pending changes
      if (!isDirty) {
        setTranslationMapData(rawData)
      } else {
        console.log('Skipping translationMapData update due to pending changes')
        // Update other fields but preserve the specification
        setTranslationMapData(prev =>
          prev ? { ...prev, ...rawData, specification: prev.specification } : rawData
        )
      }

      // Don't load implementation if user is actively editing
      if (isUserEditingRef.current) {
        return
      }

      // Load from server directly
      implementationLoadFromServerRef.current(rawData.encoded_implementation || '')

      // Always load specification from server, but preserve pending changes if they exist
      const serverSpecification = rawData.specification || {}

      // Ensure we have at least empty values for required fields
      const normalizedServerSpecification = {
        summary: serverSpecification.summary || '',
        data_mapping: serverSpecification.data_mapping || '',
        ...serverSpecification,
      }

      // If we have pending changes, preserve them but update server reference
      if (isDirty) {
        // Preserve pending changes but ensure we have the latest server data as base
        setServerSpecification(normalizedServerSpecification)
        specificationRef.current = normalizedServerSpecification
        // Keep current specification state (which has pending changes)
      } else {
        // No pending changes, load fresh from server
        setSpecification(normalizedServerSpecification)
        setServerSpecification(normalizedServerSpecification)
        specificationRef.current = normalizedServerSpecification
      }
    }
  }, [rawData, isDirty])

  // Effect to update input data model when data is fetched
  useEffect(() => {
    if (inputDataModelData) {
      // Only update if we don't have a user-selected data model or if the ID matches
      setInputDataModel(prev => {
        if (!prev || prev.id === inputDataModelData.id) {
          return inputDataModelData
        }
        return prev
      })
    }
  }, [inputDataModelData])

  // Effect to update output data model when data is fetched
  useEffect(() => {
    if (outputDataModelData) {
      // Only update if we don't have a user-selected data model or if the ID matches
      setOutputDataModel(prev => {
        if (!prev || prev.id === outputDataModelData.id) {
          return outputDataModelData
        }
        return prev
      })
    }
  }, [outputDataModelData])

  // Effect to update loading states
  useEffect(() => {
    setIsLoadingInputDataModel(isLoadingInputDataModelData)
  }, [isLoadingInputDataModelData])

  useEffect(() => {
    setIsLoadingOutputDataModel(isLoadingOutputDataModelData)
  }, [isLoadingOutputDataModelData])

  // Effect to clear data model states when IDs are cleared
  useEffect(() => {
    if (!translationMapData?.input_data_model) {
      setInputDataModel(undefined)
    }
  }, [translationMapData?.input_data_model])

  useEffect(() => {
    if (!translationMapData?.output_data_model) {
      setOutputDataModel(undefined)
    }
  }, [translationMapData?.output_data_model])

  // Note: Translation map settings update success/error handling is now done in the save callback
  // to avoid conflicts and provide better user feedback

  // Note: Implementation update success/error handling is now done in the save callback
  // to avoid conflicts and provide better user feedback

  useEffect(() => {
    if (createError) {
      toast.error(`Error when creating: ${createError}`)
      return
    }

    if (isCreated) {
      toast.success(`Translation map created successfully`)
      setIsCreationMode(false)
      // Navigate to the created translation map
      window.history.replaceState(null, '', `/translation-maps/${translationMapData?.id}`)
    }
  }, [isCreated, createError, translationMapData?.id])

  useEffect(() => {
    if (isPushError) {
      toast.error(`Error when pushing: ${pushError}`)
      return
    }

    if (isPushSuccess) {
      toast.success(`Successfully pushed`)
      // Only refetch after push operations, not during editing
      handleRefetch()
    }
  }, [isPushError, isPushSuccess])

  // Functions
  const handleRefetch = () => {
    setTimeout(() => {
      refetch()
    }, 1000)
  }

  handleExport.current = () => {
    if (!translationMapData?.id) return
    onExport(translationMapData.id, translationMapData.name)
  }

  handlePush.current = () => {
    if (translationMapData) push(translationMapData)
    else {
      toast.error(`There's no translation map selected`)
    }
  }

  handleCreate.current = () => {
    try {
      setValidations(undefined)
      validateFields(translationMapData as any)
      createTranslationMap(
        translationMapData as Omit<
          TranslationMap,
          'id' | 'created_at' | 'updated_at' | 'deleted_at'
        >
      )
    } catch (error) {
      setValidations(`Something went wrong: ${error}`)
    }
  }

  handleSave.current = async () => {
    try {
      isSavingRef.current = true
      setValidations(undefined)
      const dataToSave = translationMapData || rawData

      // Save implementation if there are changes
      if (implementation.isDirty && dataToSave?.id) {
        try {
          await implementation.save()
        } catch (error) {
          const errorMessage =
            (error as any)?.response?.data?.message ||
            (error as any)?.message ||
            'Failed to save artifact'
          toast.error(errorMessage)
          setValidations(`Failed to save artifact: ${errorMessage}`)
          return
        }
      }

      // Update translation map settings including specification
      const {
        encoded_implementation,
        specification: oldSpecification,
        ...settingsToUpdate
      } = dataToSave as any

      console.log('🟡 Save: Current specification state:', specification)
      console.log('🟡 Save: Current specification ref:', specificationRef.current)
      console.log('🟡 Save: isDirty:', isDirty)

      const settingsWithSpecification = {
        ...settingsToUpdate,
        specification: specificationRef.current,
      }
      console.log('🟡 Save: Final request payload:', settingsWithSpecification)
      updateTranslationMapSetting(settingsWithSpecification as TranslationMap, {
        onSuccess: () => {
          toast.success('All changes saved successfully')
          // Clear dirty state after successful save
          setIsDirty(false)
          setServerSpecification(specificationRef.current)
          // Clear saving flag after a short delay to allow UI to stabilize
          setTimeout(() => {
            isSavingRef.current = false
          }, 100)
        },
        onError: (error: any) => {
          console.error('Failed to update translation map settings:', error)
          const errorMessage =
            error?.response?.data?.message || error?.message || 'Failed to save settings'
          toast.error(errorMessage)
          setValidations(`Failed to save settings: ${errorMessage}`)
          // Clear saving flag on error
          isSavingRef.current = false
        },
      })
    } catch (error) {
      setValidations(`Something went wrong: ${error}`)
      // Clear saving flag on error
      isSavingRef.current = false
    }
  }

  const onFormUpdate = useCallback((updates: Partial<TranslationMap>) => {
    setIsDirty(true)
    setTranslationMapData(prev => (prev ? { ...prev, ...updates } : (updates as TranslationMap)))
  }, [])

  // Specification update function
  const updateSpecificationField = useCallback(
    (field: keyof TranslationMapSpecificationSchema, value: string) => {
      console.log('🟢 Context: updateSpecificationField called with:', field, value)
      setSpecification(prev => {
        const newSpecification = { ...prev, [field]: value }
        console.log('🟢 Context: newSpecification:', newSpecification)
        specificationRef.current = newSpecification
        return newSpecification
      })
      setIsDirty(true) // Use the main dirty state
    },
    []
  )

  // Dialog Actions
  const openInputDialog = useCallback(() => setInputDialogOpen(true), [])
  const closeInputDialog = useCallback(() => setInputDialogOpen(false), [])
  const openOutputDialog = useCallback(() => setOutputDialogOpen(true), [])
  const closeOutputDialog = useCallback(() => setOutputDialogOpen(false), [])
  const openInputDataModelStructureDialog = useCallback(
    () => setInputDataModelStructureDialogOpen(true),
    []
  )
  const closeInputDataModelStructureDialog = useCallback(
    () => setInputDataModelStructureDialogOpen(false),
    []
  )
  const openOutputDataModelStructureDialog = useCallback(
    () => setOutputDataModelStructureDialogOpen(true),
    []
  )
  const closeOutputDataModelStructureDialog = useCallback(
    () => setOutputDataModelStructureDialogOpen(false),
    []
  )

  // Data Model Actions
  const updateInputDataModel = useCallback(
    (dataModel: any) => {
      if (!translationMapData) return

      const updated = {
        ...translationMapData,
        input_data_model: dataModel?.id || null,
        input_content_type: dataModel?.engine || translationMapData.input_content_type,
      }
      onFormUpdate(updated)

      // Immediately update the input data model state to reflect the selection
      if (dataModel) {
        setInputDataModel(dataModel)
      } else {
        setInputDataModel(undefined)
      }
    },
    [translationMapData, onFormUpdate]
  )

  const updateOutputDataModel = useCallback(
    (dataModel: any) => {
      if (!translationMapData) return

      const updated = {
        ...translationMapData,
        output_data_model: dataModel?.id || null,
        output_content_type: dataModel?.engine || translationMapData.output_content_type,
      }
      onFormUpdate(updated)

      // Immediately update the output data model state to reflect the selection
      if (dataModel) {
        setOutputDataModel(dataModel)
      } else {
        setOutputDataModel(undefined)
      }
    },
    [translationMapData, onFormUpdate]
  )

  // Artifact Actions - now using the implementation hook
  const updateArtifactValue = useCallback(
    (value: string) => {
      isUserEditingRef.current = true
      implementation.updateContent(value)
      setIsDirty(true)

      // Reset editing flag after user stops typing
      setTimeout(() => {
        isUserEditingRef.current = false
      }, 1000)
    },
    [implementation]
  )

  const updateArtifactValueFromServer = useCallback(
    (value: string) => {
      isUserEditingRef.current = false
      implementation.loadFromServer(btoa(value))
      setIsDirty(false)
      setServerSpecification(specificationRef.current)
    },
    [implementation, specification]
  )

  const clearPendingChanges = useCallback(() => {
    implementation.clearPendingChanges()
    setIsDirty(false)
    setServerSpecification(specificationRef.current)
  }, [implementation])

  // Create optimized context values with focused memoization
  const coreState = useMemo(() => {
    const combinedIsDirty = isDirty
    return {
      translationMap: translationMapData || rawData,
      isDirty: combinedIsDirty,
      validations,
      viewMode,
      isLoading,
      error,
      isUpdating,
      isPushing,
      isCreating,
      onFormUpdate,
    }
  }, [
    translationMapData,
    rawData,
    isDirty,
    validations,
    viewMode,
    isLoading,
    error,
    isUpdating,
    isPushing,
    isCreating,
    onFormUpdate,
  ])

  const dataModelsState = useMemo(
    () => ({
      input: {
        dataModel: inputDataModel,
        contentType: inputDataModel?.engine || translationMapData?.input_content_type,
      },
      output: {
        dataModel: outputDataModel,
        contentType: outputDataModel?.engine || translationMapData?.output_content_type,
      },
      loading: { input: isLoadingInputDataModel, output: isLoadingOutputDataModel },
    }),
    [
      inputDataModel,
      outputDataModel,
      isLoadingInputDataModel,
      isLoadingOutputDataModel,
      translationMapData,
    ]
  )

  const dialogsState = useMemo(
    () => ({
      input: inputDialogOpen,
      output: outputDialogOpen,
      inputStructure: inputDataModelStructureDialogOpen,
      outputStructure: outputDataModelStructureDialogOpen,
    }),
    [
      inputDialogOpen,
      outputDialogOpen,
      inputDataModelStructureDialogOpen,
      outputDataModelStructureDialogOpen,
    ]
  )

  const mappingEditorState = useMemo(
    () => ({
      activeTab,
      setActiveTab,
    }),
    [activeTab, setActiveTab]
  )

  const mappingDetailsState = useMemo(
    () => ({
      currentView,
      setCurrentView,
    }),
    [currentView, setCurrentView]
  )

  const coreActions = useMemo(
    () => ({
      updateTranslationMap: onFormUpdate,
      save: () => handleSave.current(),
      create: () => handleCreate.current(),
      export: () => handleExport.current(),
      push: () => handlePush.current(),
      refetch: handleRefetch,
      setViewMode,
    }),
    [onFormUpdate, handleRefetch, setViewMode]
  )

  const dialogActions = useMemo(
    () => ({
      openInputDialog,
      closeInputDialog,
      openOutputDialog,
      closeOutputDialog,
      openInputDataModelStructureDialog,
      closeInputDataModelStructureDialog,
      openOutputDataModelStructureDialog,
      closeOutputDataModelStructureDialog,
    }),
    [
      openInputDialog,
      closeInputDialog,
      openOutputDialog,
      closeOutputDialog,
      openInputDataModelStructureDialog,
      closeInputDataModelStructureDialog,
      openOutputDataModelStructureDialog,
      closeOutputDataModelStructureDialog,
    ]
  )

  const dataModelActions = useMemo(
    () => ({
      updateInputDataModel,
      updateOutputDataModel,
    }),
    [updateInputDataModel, updateOutputDataModel]
  )

  const artifactActions = useMemo(
    () => ({
      updateArtifactValue,
      updateArtifactValueFromServer,
      currentArtifactValue: implementation.content,
      clearPendingChanges,
    }),
    [
      updateArtifactValue,
      updateArtifactValueFromServer,
      implementation.content,
      clearPendingChanges,
    ]
  )

  const specificationActions = useMemo(() => {
    return {
      updateSpecification: (spec: TranslationMapSpecificationSchema) => {
        // Update each field individually to trigger dirty state
        Object.entries(spec).forEach(([field, value]) => {
          updateSpecificationField(
            field as keyof TranslationMapSpecificationSchema,
            value as string
          )
        })
      },
      updateSpecificationField: (field: string, value: string) => {
        updateSpecificationField(field as keyof TranslationMapSpecificationSchema, value)
      },
      currentSpecification: specification,
      isSpecificationDirty: isDirty,
      clearSpecificationPendingChanges: () => {
        setIsDirty(false)
        setServerSpecification(specificationRef.current)
      },
    }
  }, [specification, isDirty, updateSpecificationField])

  // Combine all state and actions into the final context value
  const contextValue: TranslationMapDetailsContextProps = useMemo(
    () => ({
      ...coreState,
      dataModels: dataModelsState,
      dialogs: dialogsState,
      mappingEditor: mappingEditorState,
      mappingDetails: mappingDetailsState,
      ...coreActions,
      ...dialogActions,
      ...dataModelActions,
      ...artifactActions,
      ...specificationActions,
    }),
    [
      coreState,
      dataModelsState,
      dialogsState,
      mappingEditorState,
      mappingDetailsState,
      coreActions,
      dialogActions,
      dataModelActions,
      artifactActions,
      specificationActions,
    ]
  )

  return (
    <TranslationMapDetailsContext.Provider value={contextValue}>
      {children}
    </TranslationMapDetailsContext.Provider>
  )
}

export function useTranslationMapDetailsContext() {
  const context = useContext(TranslationMapDetailsContext)
  if (context === undefined) {
    throw new Error(
      'useTranslationMapDetailsContext must be used within a TranslationMapDetailsProvider'
    )
  }
  return context
}
